import { useI18n } from "@baton/i18n";
import { Drawer, frameGroundClassName, frameSheetClassName, useScrollFade } from "@baton/ui";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import "../shell.css";

/**
 * FR-015-002, `contracts/design-system.md` § The shell, `contracts/screens.md` § The rail
 * and the work surface: the three-column grid on the dark canvas, the rail fixed at 64 px
 * and never scrolling, the sidebar at 260 px resizable between 260 and 360 with the width
 * the host persists, and the work surface as one panel inset 8 px on all four edges at 16 px
 * radius with a one-pixel border. The chrome is dark under both
 * themes; the work surface is the only themed surface. Below `--bp-lg` the sidebar is a
 * drawer over the surface and the rail stays; at `--bp-md` and below the inset is dropped.
 */
export const SIDEBAR_MIN = 260;
export const SIDEBAR_MAX = 360;
export const SIDEBAR_DEFAULT = 260;
export const SIDEBAR_KEYBOARD_STEP = 16;
const LARGE_QUERY = "(min-width: 1024px)";
/** Below `--bp-md` the rail leaves the frame so the work surface fills a phone's width; it rides in the drawer beside the sidebar instead (DESIGN.md § Layout; `research.md` W-30, the smaller points of that audit). */
const NARROW_QUERY = "(width < 768px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** The longest a sidebar move takes (`--duration-slow`, with room), after which it is settled whether or not an animation ran. */
const MOVE_MS = 400;
/** How long the pointer rests on the rail before the collapsed sidebar peeks, so a pointer crossing it never opens it; and how long the peek stays after the pointer leaves, so a gap never closes it. */
const PEEK_AFTER_MS = 150;
const PEEK_LEAVE_MS = 120;

export function clampSidebarWidth(width: number): number {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(width)));
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    list.addEventListener("change", listener);
    return () => {
      list.removeEventListener("change", listener);
    };
  }, [query]);
  return matches;
}

export function useIsLarge(): boolean {
  return useMediaQuery(LARGE_QUERY);
}

export interface AppShellProps {
  /** Absent on a frame outside the workspace prefix, which has no modes; the grid then drops the rail's column. */
  readonly rail?: ReactNode;
  readonly sidebar: ReactNode;
  readonly topBar: ReactNode;
  readonly banner?: ReactNode;
  readonly peek?: ReactNode;
  /** 019's assistant panel, docked at the work surface's end after the peek (`contracts/design-system.md` § Assistant panel, 2026-10-14). */
  readonly dock?: ReactNode;
  readonly sidebarWidth: number;
  readonly onSidebarWidthChange: (width: number) => void;
  /** The sidebar collapsed at `--bp-lg` and up: its column goes, the rail is the one strip, and resting the pointer on the rail or focusing it peeks the sidebar over the sheet (2026-09-17, by the owner's decision); below `--bp-lg` the drawer is unaffected. */
  readonly sidebarCollapsed?: boolean;
  readonly drawerOpen: boolean;
  readonly onDrawerOpenChange: (open: boolean) => void;
  readonly children: ReactNode;
}

export function AppShell({ rail, sidebar, topBar, banner, peek, dock, sidebarWidth, onSidebarWidthChange, sidebarCollapsed = false, drawerOpen, onDrawerOpenChange, children }: AppShellProps) {
  const { t } = useI18n();
  const large = useIsLarge();
  // The main region fades at an edge the page continues past, over the bulk bar while it stands at the foot (`useScrollFade`, 2026-10-21).
  const main = useScrollFade<HTMLElement>({ over: '[data-slot="bulk-bar"]' });
  const narrow = useMediaQuery(NARROW_QUERY);
  const framedRail = rail !== undefined && !narrow;
  const dragging = useRef<{ startX: number; startWidth: number } | null>(null);
  const widthChange = useRef(onSidebarWidthChange);
  useEffect(() => {
    widthChange.current = onSidebarWidthChange;
  }, [onSidebarWidthChange]);
  const [handlers] = useState(() => {
    const move = (event: PointerEvent) => {
      const drag = dragging.current;
      if (!drag) return;
      const rtl = getComputedStyle(document.documentElement).direction === "rtl";
      const delta = (event.clientX - drag.startX) * (rtl ? -1 : 1);
      widthChange.current(clampSidebarWidth(drag.startWidth + delta));
    };
    const stop = () => {
      dragging.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    return { move, stop };
  });
  const stopDrag = useCallback(() => {
    handlers.stop();
  }, [handlers]);
  useEffect(() => stopDrag, [stopDrag]);

  const width = clampSidebarWidth(sidebarWidth);
  const wanted = large && sidebarCollapsed;
  // The grid takes the wanted shape at once; the sidebar and the sheet then move into it, as the rail's flyout moved them (2026-09-17, by the owner's decision): collapsing, the sidebar slides out behind the rail while the sheet slides across into the room it leaves; expanding, the sidebar slides in while the sheet slides back. `settled` is the state the last such move finished in; reduced motion settles at once.
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [settled, setSettled] = useState(wanted);
  const collapsed = wanted;
  const moving = !reducedMotion && settled !== wanted;
  // Pinned from an open peek, the sidebar is already where it will dock and the sheet already beside it, so that expand plays no move at all (2026-09-17, by the owner's report of a second slide).
  const [pinned, setPinned] = useState(false);
  const move = moving ? (wanted ? "out" : pinned ? "pin" : "in") : undefined;
  // Unpinned from the docked sidebar, the sidebar stays where it is as the peek until the pointer leaves it (2026-09-17, by the owner's decision), so the unpin settles at once and the peek is held open without a slide.
  const [held, setHeld] = useState(false);
  // Pinned from the peek, the sheet keeps its dim while the pointer is still on the sidebar and drops it only once the pointer leaves, so pinning changes nothing but the pin until you move away (2026-09-17, by the owner's decision).
  const [dimHeld, setDimHeld] = useState(false);
  // For one frame after a pin or an unpin the sheet drops or takes the peek's offset without a transition, since its column moved by the same width the other way.
  const [snap, setSnap] = useState(false);
  useEffect(() => {
    if (!snap) return;
    const frame = requestAnimationFrame(() => {
      setSnap(false);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [snap]);
  const settle = () => {
    setSettled(wanted);
  };
  // The move settles on its animation's end, and by the animation's length regardless, so a document without animations (or one that cancelled it) never leaves the sidebar half-way.
  useEffect(() => {
    if (!moving) return;
    const id = setTimeout(() => {
      setSettled(wanted);
      setPinned(false);
    }, MOVE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [moving, wanted]);

  // The peek: opens after the pointer rests on the rail or the focus enters it, stays while the pointer or the focus is on the rail or the peek, slides out when a row is chosen or the pointer leaves.
  const [peekOpen, setPeekOpen] = useState(false);
  const [peekClosing, setPeekClosing] = useState(false);
  useEffect(() => {
    if (!peekClosing) return;
    const id = setTimeout(() => {
      setPeekClosing(false);
    }, MOVE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [peekClosing]);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schedulePeek = useCallback(
    (next: boolean, after: number) => {
      if (peekTimer.current !== null) clearTimeout(peekTimer.current);
      peekTimer.current = setTimeout(() => {
        peekTimer.current = null;
        setPeekOpen((current) => {
          if (current && !next && !reducedMotion) setPeekClosing(true);
          if (!next) setHeld(false);
          return next;
        });
      }, after);
    },
    [reducedMotion],
  );
  useEffect(
    () => () => {
      if (peekTimer.current !== null) clearTimeout(peekTimer.current);
    },
    [],
  );
  const peekHandlers = collapsed
    ? {
        onPointerEnter: (event: React.PointerEvent) => {
          // While the sidebar is sliding out it is a child of the rail's box, so the pointer that pressed its toggle is "inside"; that is not a rest on the rail.
          if (event.pointerType === "mouse" && move !== "out") schedulePeek(true, PEEK_AFTER_MS);
        },
        onPointerLeave: () => {
          schedulePeek(false, PEEK_LEAVE_MS);
        },
        onFocus: () => {
          schedulePeek(true, 0);
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
          if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) schedulePeek(false, 0);
        },
      }
    : {};
  return (
    <div className={`b-shell ${frameGroundClassName}`} data-chrome data-rail={framedRail ? undefined : "none"} data-sidebar={collapsed ? "collapsed" : undefined} data-move={move} data-peek={collapsed && peekOpen ? "open" : undefined} data-dim={!collapsed && dimHeld ? "true" : undefined} data-snap={snap ? "true" : undefined} style={{ "--shell-sidebar-width": `${width}px` } as React.CSSProperties}>
      <a href="#main" className="b-shell__skip">
        {t("shell.skipToContent")}
      </a>
      {/* The offline or connection banner is a hard strip across the whole page, above the rail, the sidebar and the sheet (2026-09-17, night, by the owner's decision: inside the sheet it read as part of the screen). */}
      {banner !== undefined && banner !== null && banner !== false && <div className="b-shell__banner">{banner}</div>}
      {framedRail && (
        <div className="b-shell__rail" {...peekHandlers}>
          {rail}
          {collapsed && (move === "out" || peekOpen || peekClosing) && (
            <div
              className="b-shell__sidebar b-shell__sidebar--peek"
              data-state={held ? "held" : move === "out" ? "out" : peekOpen ? "open" : "closing"}
              onAnimationEnd={(event) => {
                // Only the panel's own slide, not a row's or the tree's animation bubbling up.
                if (event.target !== event.currentTarget) return;
                if (move === "out") settle();
                else if (!peekOpen) setPeekClosing(false);
              }}
              onClickCapture={(event) => {
                // A chosen row navigates, and the toggle pins the sidebar open; the peek goes with either.
                if (!(event.target instanceof Element)) return;
                if (event.target.closest(".b-sidebar__collapse") !== null) {
                  setPinned(true);
                  setSnap(true);
                  setDimHeld(true);
                }
                if (event.target.closest("a, .b-sidebar__collapse") !== null) schedulePeek(false, 0);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") schedulePeek(false, 0);
              }}
            >
              {sidebar}
            </div>
          )}
        </div>
      )}
      {collapsed ? null : large ? (
        <div
          className="b-shell__sidebar"
          data-move={move}
          onClickCapture={(event) => {
            // Unpinned: the sidebar stays as the peek, held open, until the pointer leaves it.
            if (event.target instanceof Element && event.target.closest(".b-sidebar__collapse") !== null) {
              setSettled(true);
              setHeld(true);
              setPeekOpen(true);
              setSnap(true);
              setDimHeld(false);
            }
          }}
          onPointerLeave={() => {
            setDimHeld(false);
          }}
          onBlur={(event: React.FocusEvent<HTMLElement>) => {
            if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDimHeld(false);
          }}
          onAnimationEnd={(event) => {
            // Only the sidebar's own slide settles the move, not a row's or the tree's animation bubbling up.
            if (moving && event.target === event.currentTarget) {
              settle();
              setPinned(false);
            }
          }}
        >
          {sidebar}
          <button
            type="button"
            className="b-shell__resize"
            role="separator"
            aria-orientation="vertical"
            aria-label={t("shell.sidebar.resize")}
            aria-valuemin={SIDEBAR_MIN}
            aria-valuemax={SIDEBAR_MAX}
            aria-valuenow={width}
            onPointerDown={(event) => {
              dragging.current = { startX: event.clientX, startWidth: width };
              window.addEventListener("pointermove", handlers.move);
              window.addEventListener("pointerup", handlers.stop);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") onSidebarWidthChange(clampSidebarWidth(width + SIDEBAR_KEYBOARD_STEP));
              if (event.key === "ArrowLeft") onSidebarWidthChange(clampSidebarWidth(width - SIDEBAR_KEYBOARD_STEP));
              if (event.key === "Home") onSidebarWidthChange(SIDEBAR_MIN);
              if (event.key === "End") onSidebarWidthChange(SIDEBAR_MAX);
            }}
          />
        </div>
      ) : (
        <Drawer open={drawerOpen} onOpenChange={onDrawerOpenChange} side="start" size="sm" title={t("shell.sidebar.title")} className="b-shell__drawer">
          {narrow && rail !== undefined ? (
            <div className="b-shell__drawer-frame">
              {rail}
              <div className="b-shell__drawer-tree">{sidebar}</div>
            </div>
          ) : (
            sidebar
          )}
        </Drawer>
      )}
      <div className={`b-shell__surface ${frameSheetClassName}`} data-large={large ? "true" : undefined}>
        {topBar}
        <div className="b-shell__work">
          <main ref={main} id="main" className="b-shell__main" tabIndex={-1}>
            {children}
          </main>
          {peek}
          {dock}
        </div>
      </div>
    </div>
  );
}
